/** Shared display formatting for Overview tables, cards and PDF renders. */

const DASH_VALUE = /^[-–—]$/;

/** Remove the presentation-only project-name marker from persisted/editable text. */
export function normalizeProjectName(value: string | null | undefined): string | null {
  const raw = String(value ?? '')
    .replace(/\s+\*\s*$/, '')
    .trim();
  return raw || null;
}

export function isBlankOrDash(value: unknown): boolean {
  return value == null || String(value).trim() === '' || DASH_VALUE.test(String(value).trim());
}

/** Target preview uses TBA for an unknown/dash value. */
export function formatTargetPreview(value: string | null | undefined): string {
  const raw = String(value ?? '').trim();
  return !raw || DASH_VALUE.test(raw) ? 'TBA' : raw;
}

/** Land cost uses NIL for an unknown/dash value. */
export function formatLandCost(value: string | null | undefined): string {
  const raw = String(value ?? '').trim();
  if (!raw || DASH_VALUE.test(raw)) return 'NIL';
  // Cards append the unit suffix themselves; strip an already-present suffix
  // so source values such as "1,200 psf ppr" are never duplicated.
  return raw.replace(/\s*(?:p\.?\s*s\.?\s*f\.?(?:\s+ppr)?)\s*$/i, '').trim() || 'NIL';
}

/**
 * Canonicalise launch price values without duplicating currency or psf suffixes.
 * Non-numeric labels are intentionally preserved for manual editorial values.
 */
export function formatEstLaunchPrice(value: string | null | undefined): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '–';
  if (DASH_VALUE.test(raw)) return raw;
  const stripped = raw
    .replace(/^(?:S\$|\$)\s*/i, '')
    .replace(/\s*(?:p\.?\s*s\.?\s*f\.?(?:\s+ppr)?|per\s+sq\.?\s*ft)\s*$/i, '')
    .replace(/,/g, '')
    .trim();
  const numeric = Number(stripped);
  if (Number.isFinite(numeric)) {
    return `$${numeric.toLocaleString('en-SG', { maximumFractionDigits: 2 })} psf`;
  }
  return raw;
}

/** Value suitable for storing after an Est. Avg Launch Price content edit. */
export function normalizeEstLaunchPrice(value: string | null | undefined): string | null {
  const raw = String(value ?? '')
    .replace(/^Est\.\s*Avg\s*Launch\s*/i, '')
    .trim();
  if (!raw || DASH_VALUE.test(raw)) return null;
  return formatEstLaunchPrice(raw);
}

/** Remove presentation-only text from an editable target preview. */
export function normalizeTargetPreview(value: string | null | undefined): string | null {
  const raw = String(value ?? '').replace(/^Target Preview:\s*/i, '').trim();
  return !raw || DASH_VALUE.test(raw) || /^TBA$/i.test(raw) ? null : raw;
}

/** Remove presentation-only text from an editable land-cost value. */
export function normalizeLandCost(value: string | null | undefined): string | null {
  const raw = String(value ?? '')
    .replace(/^(?:Est\.\s*)?Land Cost\s*/i, '')
    .replace(/\s*p\.?\s*s\.?\s*f\.?\s*ppr$/i, '')
    .trim();
  return !raw || DASH_VALUE.test(raw) || /^NIL$/i.test(raw) ? null : raw;
}
